// @ts-check

import React from "react";
import { useNavigate } from "react-router-dom";
import { BlockStack, Button, IndexTable, InlineStack, Link, Text, Tooltip, useBreakpoints } from "@shopify/polaris";
import { FilterIcon } from "@shopify/polaris-icons";

import { formatInTimeZone } from "date-fns-tz";

import { useGlobalContext } from "context/GlobalContext";

import { useFormatCurrency } from "hooks/use-format-currency";

import { UTM_FIELDS } from "utils/utm-sets";

import "./OrdersTableRow.css";

const MobileOrdersTableRow = ({
  id,
  position,
  data,
}) => {
  const formatCurrency = useFormatCurrency();
  const { store } = useGlobalContext();

  const formattedOrders = (
    <Text as="span" variant="bodyMd" fontWeight="semibold">
      {data.name}
    </Text>
  );

  const formattedAmount = formatCurrency(data.shop_money_order_amount, {
    currency: data.shop_money_currency_code,
  });

  const formattedDate = store ? formatInTimeZone(new Date(data.order_created_at), store.iana_timezone, "MMM do 'at' h':'mmaaa") : "...";

  return (
    <IndexTable.Row id={id} position={position}>
      <div style={{ padding: "12px 16px", width: "100%" }}>
        <BlockStack gap="200">
          <InlineStack align="space-between" gap="200">
            <Text as="span" variant="bodySm" tone="subdued">
              {formattedOrders} • {formattedDate}
            </Text>

            <Text variant="bodyMd" as="span" fontWeight="semibold" alignment="end" numeric>
              {formattedAmount}
            </Text>
          </InlineStack>

          {UTM_FIELDS.map(({ label, key }) => (
            <BlockStack key={key}>
              <Text as="p" variant="bodyMd" fontWeight="semibold">
                {label}
              </Text>

              <Text as="p" variant="bodyMd">
                {data[key] || "-"}
              </Text>
            </BlockStack>
          ))}

          <BlockStack>
            <Text as="p" variant="bodyMd" fontWeight="semibold">
              Customer
            </Text>

            <Link
              removeUnderline
              onClick={() => {
                const customerId = data.customer_shopify_id.split("Customer/")[1];
                open(`shopify://admin/customers/${customerId}`, '_top');
              }}
            >
              <Text variant="bodyMd" as="span">{data.customer_name || "-"}</Text>
            </Link>
          </BlockStack>
        </BlockStack>
      </div>
    </IndexTable.Row>
  );
};

const LINKABLE_VALUES = [
  {
    key: "utm_source",
    createHref: (value) => `/reports/source/${encodeURIComponent(value)}`,
  },
  {
    key: "utm_medium",
    createHref: (value) => `/reports/medium/${encodeURIComponent(value)}`,
  },
  {
    key: "utm_campaign",
    createHref: (value) => `/reports/campaign/${encodeURIComponent(value)}`,
  },
];

const TRUNCATE_LENGTH = 40;

export const OrdersTableRow = ({
  data,
  index,
  columns,
  sortedValues,
  setFilterValue,
  removeFilter,
}) => {
  const {
    name,
    order_created_at,
    shop_money_order_amount,
    shop_money_currency_code,
    order_id,
    customer_name,
    customer_shopify_id,
  } = data;

  const formatCurrency = useFormatCurrency();
  const { store } = useGlobalContext();
  const navigate = useNavigate();

  const handleOrderClick = () => {
    const orderId = order_id.split("Order/")[1];
    open(`shopify://admin/orders/${orderId}`, '_top');
  };

  let customerColumn;
  if (columns.includes("customer")) {
    customerColumn = (
      <IndexTable.Cell>
        <Link
          removeUnderline
          onClick={() => {
            const customerId = customer_shopify_id?.split("Customer/")[1];
            open(`shopify://admin/customers/${customerId}`, '_top');
          }}
        >
          <Text variant="bodyMd" as="span">{customer_name || "-"}</Text>
        </Link>
      </IndexTable.Cell>
    );
  }

  const utmColumns = columns
    .filter((col) => UTM_FIELDS.map(({ key }) => key).includes(col))
    .slice()
    .sort((a, b) => sortedValues.indexOf(a) - sortedValues.indexOf(b))
    .map((col) => {
      let value = data[col] || "-";
      const shouldTruncate = data[col] && data[col].length > TRUNCATE_LENGTH;

      if (shouldTruncate) {
        value = (
          <Text as="span">
            {value.substring(0, TRUNCATE_LENGTH).trim()}...
          </Text>
        );
      }

      let filterButton;
      if (data[col]) {
        filterButton = (
          <div className="filter-button-container">
            <Tooltip content="Filter by this value" dismissOnMouseOut>
              <Button
                size="micro"
                icon={FilterIcon}
                onClick={() => {
                  setFilterValue(col, data[col]);
                }}
              />
            </Tooltip>
          </div>
        );

        const linkableValue = LINKABLE_VALUES.find(({ key }) => key === col);

        if (linkableValue) {
          const handleClick = () => {
            navigate(linkableValue.createHref(data[col]));
          }

          value = (
            <Link
              onClick={handleClick}
              removeUnderline
            >
              {value}
            </Link>
          );
        }
      }

      let truncateWrapped = value;
      if (shouldTruncate) {
        truncateWrapped = (
          <Tooltip content={data[col]} dismissOnMouseOut>
            {value}
          </Tooltip>
        )
      }

      return (
        <IndexTable.Cell key={col}>
          <div className="utm-table-cell">
            {truncateWrapped}
            {filterButton}
          </div>
        </IndexTable.Cell>
      )
    });

  const formattedAmount = formatCurrency(shop_money_order_amount, {
    currency: shop_money_currency_code,
  });

  if (useBreakpoints().smDown) {
    return (
      <MobileOrdersTableRow
        id={order_id}
        position={index}
        data={data}
      />
    )
  }

  const formattedDate = store ? formatInTimeZone(new Date(order_created_at), store.iana_timezone, "MMM do 'at' h':'mmaaa") : "...";

  return (
    <IndexTable.Row id={order_id} position={index}>
      <IndexTable.Cell>
        <Link
          removeUnderline
          onClick={handleOrderClick}
        >
          <Text variant="bodyMd" as="span">{name}</Text>
        </Link>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" as="span">
          {formattedDate}
        </Text>
      </IndexTable.Cell>
      {customerColumn}
      {utmColumns}
      <IndexTable.Cell>
        <Text variant="bodyMd" as="span" fontWeight="semibold" alignment="end" numeric>
          {formattedAmount}
        </Text>
      </IndexTable.Cell>
    </IndexTable.Row>
  );
};
